import { Injectable, NgZone } from "@angular/core";
import { Router } from "@angular/router";
import { Action, Selector, State, StateContext, Store } from "@ngxs/store";
import { EStatementModel } from "@shared/interfaces/e-statement";
import { ELearningAgent } from "@shared/interfaces/profile.model";
import { UserResponse } from "@shared/interfaces/user.model";
import { catchError, map } from "rxjs/operators";

import {
  GET_AGENT_LIST,
  GET_ESTATEMENT_LOV,
  GET_USER_AGENT_LIST,
  GET_USER_BRANCHLIST,
  GET_YEAR_LIST,
  POST_AGENT_DETAIL,
  RESET_ESTATEMENT,
} from "./e-statement.action";
import { EStatementService } from "./e-statement.service";

@State<EStatementModel>({
  name: "EStatementState",
  defaults: {
    agentDetail: null,
    userBranchList: null,
    yearList: null,
    agentList: {
      disabledLoading: false,
      data: null,
    },
    eStatementLov: null,
    adminStatus: false,
    showEstatement: null,
  },
})
@Injectable()
export class EStatementState {
  constructor(
    private readonly eStatementService: EStatementService,
    private readonly store: Store,
    private readonly router: Router,
    private readonly ngZone: NgZone,
  ) {}

  private normalizeAgentList(agentList: ELearningAgent[] | null | undefined) {
    return (agentList ?? []).map((agent) => ({
      ...agent,
      code: agent.code ?? agent.agentCode,
    }));
  }

  private isProfileAdminUser(): boolean {
    const user = this.store.selectSnapshot<UserResponse | null>(
      (state) => state.UserState?.user ?? null,
    );
    return !!(
      user?.IsHQAdmin ||
      user?.isHqAdmin ||
      user?.hqAdmin ||
      user?.isBranchAdmin ||
      user?.branchAdmin ||
      user?.isBDMAdmin ||
      user?.bdmadmin
    );
  }

  @Selector()
  public static agentDetail(state: EStatementModel) {
    return state.agentDetail;
  }

  @Selector()
  public static userBranchList(state: EStatementModel) {
    return state.userBranchList;
  }

  @Selector()
  public static yearList(state: EStatementModel) {
    return state.yearList;
  }

  @Selector()
  public static agentList(state: EStatementModel) {
    return state.agentList;
  }

  @Selector()
  public static adminStatus(state: EStatementModel) {
    return state.adminStatus;
  }

  @Selector()
  public static showEstatement(state: EStatementModel) {
    return state.showEstatement;
  }

  @Action(POST_AGENT_DETAIL)
  public postAgentDetail(
    {
      patchState,
      getState,
      dispatch: _dispatch,
    }: StateContext<EStatementModel>,
    { payload }: POST_AGENT_DETAIL,
  ) {
    const _state = getState();
    return this.eStatementService.postAgentDetail(payload).pipe(
      map((res) => {
        patchState({
          agentDetail: res.data,
        });
      }),
      catchError((_err) => {
        return null;
      }),
    );
  }

  @Action(GET_USER_BRANCHLIST)
  public getUserBranchList(
    {
      patchState,
      getState,
      dispatch: _dispatch,
    }: StateContext<EStatementModel>,
    { payload }: GET_USER_BRANCHLIST,
  ) {
    const _state = getState();
    return this.eStatementService.getUserBranchList(payload).pipe(
      map((res) => {
        patchState({
          userBranchList: res.data.userBranchList,
        });
      }),
      catchError((_err) => {
        return null;
      }),
    );
  }

  @Action(GET_YEAR_LIST)
  public getYearList(
    {
      patchState,
      getState,
      dispatch: _dispatch,
    }: StateContext<EStatementModel>,
    { payload }: GET_YEAR_LIST,
  ) {
    const _state = getState();
    return this.eStatementService.getEStatementYearList(payload).pipe(
      map((res) => {
        patchState({
          yearList: res.estatemtYearList,
        });
      }),
      catchError((_err) => {
        return null;
      }),
    );
  }

  @Action(GET_AGENT_LIST)
  public getAgentList(
    {
      patchState,
      getState,
      dispatch: _dispatch,
    }: StateContext<EStatementModel>,
    { payload }: GET_AGENT_LIST,
  ) {
    const _state = getState();
    return this.eStatementService.getAgentList(payload).pipe(
      map((res) => {
        const normalizedAgentList = this.normalizeAgentList(
          res?.data?.agentList,
        );
        patchState({
          agentList: {
            disabledLoading: false,
            data: normalizedAgentList,
          },
        });
      }),
      catchError((_err) => {
        return null;
      }),
    );
  }

  @Action(GET_USER_AGENT_LIST)
  public getUserAgentList(
    {
      patchState,
      getState,
      dispatch: _dispatch,
    }: StateContext<EStatementModel>,
    { payload }: GET_USER_AGENT_LIST,
  ) {
    const _state = getState();
    patchState({ agentList: { disabledLoading: true } });
    return this.eStatementService.getUserAgentList(payload).pipe(
      map((res) => {
        const normalizedAgentList = this.normalizeAgentList(
          res?.data?.agentList,
        );
        patchState({
          agentList: {
            disabledLoading: false,
            data: normalizedAgentList,
          },
        });
      }),
      catchError((_err) => {
        return null;
      }),
    );
  }

  @Action(GET_ESTATEMENT_LOV)
  public getEStatementLov(
    {
      patchState,
      getState,
      dispatch: _dispatch,
    }: StateContext<EStatementModel>,
    { payload }: GET_ESTATEMENT_LOV,
  ) {
    const _state = getState();
    patchState({ agentList: { disabledLoading: true } });
    return this.eStatementService.getEstatementLov(payload).pipe(
      map((res) => {
        const normalizedAgentList = this.normalizeAgentList(res?.agentList);
        const isProfileAdmin = this.isProfileAdminUser();
        if (res.showEstatement) {
          patchState({
            showEstatement: res.showEstatement,
          });
          if (res.userBranchList && isProfileAdmin) {
            patchState({
              adminStatus: true,
              eStatementLov: res,
              agentList: {
                disabledLoading: false,
                data: null,
              },
              userBranchList: res.userBranchList,
              yearList: res.eStatementsYearList,
            });
          } else {
            patchState({
              adminStatus: false,
              eStatementLov: res,
              agentList: {
                disabledLoading: false,
                data: normalizedAgentList,
              },
              yearList: res.eStatementsYearList,
            });
          }
        } else {
          patchState({
            showEstatement: res.showEstatement,
            adminStatus: false,
          });
        }
      }),
      catchError((_err) => {
        return null;
      }),
    );
  }

  @Action(RESET_ESTATEMENT)
  public resetEstetement({
    patchState,
    getState,
    dispatch: _dispatch,
  }: StateContext<EStatementModel>) {
    const _state = getState();
    patchState({
      eStatementLov: null,
      userBranchList: null,
      agentList: null,
      yearList: null,
    });
  }
}
